import React, { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import ConfirmationDialog from '../../ConfirmationDialog';
import SelectMenu from '../../forms/SelectMenu';
import * as passengerSlice from '../../../data/passenger.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  DEFAULT_SERVICE_LEVEL,
  PassengerPreference,
  PassengerPreferenceBC,
} from '../../../data/models/passenger.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

const PREFIX = 'PassengerProfileDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: 444,
    maxWidth: 460,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(0),
    maxWidth: 164,
    flexDirection: 'column',
    gap: theme.spacing(3),
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(0),
    maxWidth: 164,
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

interface PassengerPreferencesDialogProps {
  passengerUUID: string;
  passengerPreferences: PassengerPreference[];
  open: boolean;
  close: () => void;
  isMemberPortal?: boolean;
}

export default function PassengerPreferencesDialog({
  passengerUUID,
  passengerPreferences,
  open,
  close,
  isMemberPortal = false,
}: PassengerPreferencesDialogProps) {
  const t = useCommonTranslation();
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();

  const [confirmDialogTitle, setConfirmDialogTitle] = React.useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = React.useState('');
  const [confirmDialogPostiveFn, setConfirmDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogNegativeFn, setConfirmDialogNegativeFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const savingPreferences = useAppSelector(
    passengerSlice.selectSavingCurrentPassengerPreferences,
  );
  const savedPreferences = useAppSelector(
    passengerSlice.selectSavedCurrentPassengerPreferences,
  );

  const getPreferenceValue = (bc: string, dflt: string): string => {
    return (
      passengerPreferences.find(
        (pp) => pp.passengerProfileAttributeTypeBC === bc,
      )?.attributeValue || dflt
    );
  };

  const getPreferenceValues = () => {
    return {
      communication: getPreferenceValue(
        PassengerPreferenceBC.communication,
        '',
      ),
      language: getPreferenceValue(PassengerPreferenceBC.language, 'en'),
      provider: getPreferenceValue(PassengerPreferenceBC.provider, 'NONE'),
      serviceLevel: getPreferenceValue(
        PassengerPreferenceBC.serviceLevel,
        DEFAULT_SERVICE_LEVEL,
      ),
      vip: getPreferenceValue(PassengerPreferenceBC.vip, 'NO'),
    };
  };

  const {
    formState: { isDirty, isValid },
    control,
    reset,
    handleSubmit,
  } = useForm({
    mode: 'onTouched',
    defaultValues: getPreferenceValues(),
  });

  useEffect(() => {
    if (passengerPreferences) {
      reset(getPreferenceValues());
    }
  }, [passengerPreferences]);

  useEffect(() => {
    if (savedPreferences) {
      confirm();
    }
  }, [savedPreferences]);

  const cancel = () => {
    setConfirmDialogOpen(false);
  };

  const confirm = () => {
    reset();
    close();
    setConfirmDialogOpen(false);
  };

  const closeModal = () => {
    setConfirmDialogOpen(true);
    setConfirmDialogTitle(t('passenger.confirmCancelCreatePassengerTitle'));
    setConfirmDialogMessage(t('passenger.confirmCancelCreatePassengerMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
  };

  const isFormDirty = () => {
    if (isDirty) {
      closeModal();
    } else {
      confirm();
    }
  };

  const getPreferenceID = (bc: PassengerPreferenceBC): number | undefined => {
    const pref = passengerPreferences.find(
      (pp) => pp.passengerProfileAttributeTypeBC === bc,
    );
    return pref?.passengerProfileAttributeID;
  };

  const getPreference = (
    bc: PassengerPreferenceBC,
    value: string | null,
  ): PassengerPreference => {
    const id = getPreferenceID(bc);
    const pref: PassengerPreference = {
      passengerProfileAttributeTypeBC: bc,
      attributeValue: value,
    };
    if (id) {
      pref.passengerProfileAttributeID = id;
    }
    return pref;
  };

  const getPreferencesPayload = (data: any): PassengerPreference[] => {
    const providerVal = data.provider === 'NONE' ? null : data.provider;
    let payload: PassengerPreference[] = [
      getPreference(PassengerPreferenceBC.communication, data.communication),
      getPreference(PassengerPreferenceBC.language, data.language),
    ];
    if (providerVal) {
      payload.push(getPreference(PassengerPreferenceBC.provider, providerVal));
    }
    if (!isMemberPortal) {
      payload = [
        ...payload,
        getPreference(PassengerPreferenceBC.serviceLevel, data.serviceLevel),
        getPreference(PassengerPreferenceBC.vip, data.vip),
      ];
    }
    return payload;
  };

  const handleSave = (data: any) => {
    const payload = getPreferencesPayload(data);
    dispatch(
      passengerSlice.savePassengerPreferencesAsync(passengerUUID, payload),
    );
  };

  return (
    <RootDialog open={open} onClose={isFormDirty}>
      <DialogTitle>
        {t('passenger.editPreferences')}
        {savingPreferences && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <SelectMenu
          label={t('passenger.communication')}
          name="communication"
          values={[
            { value: 'SMS', label: 'SMS' },
            { value: 'VOICE', label: 'Voice' },
          ]}
          valueAttr="value"
          labelAttr="label"
          required={false}
          control={control}
        />
        <SelectMenu
          label={t('passenger.language')}
          name="language"
          values={[
            { value: 'en', label: t('passenger.english') },
            { value: 'es', label: t('passenger.spanish') },
          ]}
          valueAttr="value"
          labelAttr="label"
          required={false}
          control={control}
          disabled={true}
        />
        <SelectMenu
          label={t('passenger.provider')}
          name="provider"
          values={[
            { value: 'NONE', label: t('passenger.none') },
            { value: 'UBER', label: t('passenger.uber') },
            { value: 'LYFT', label: t('passenger.lyft') },
          ]}
          valueAttr="value"
          labelAttr="label"
          required={false}
          control={control}
        />
        {!isMemberPortal && (
          <>
            <SelectMenu
              label={t('passenger.serviceLevel')}
              name="serviceLevel"
              values={[
                { value: 'C2C', label: t('passenger.c2c') },
                { value: 'D2D', label: t('passenger.d2d') },
                { value: 'WHEELCHAIR', label: t('passenger.wheelchair') },
              ]}
              valueAttr="value"
              labelAttr="label"
              required={true}
              rules={{ required: requiredFieldMessage }}
              control={control}
            />
            <SelectMenu
              label={t('passenger.vip')}
              name="vip"
              values={[
                { value: 'YES', label: t('common.yes') },
                { value: 'NO', label: t('common.no') },
              ]}
              valueAttr="value"
              labelAttr="value"
              required={false}
              control={control}
            />
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          data-fs-element="EditPreferencesSubmitButton"
          disabled={!isValid || !isDirty || savingPreferences}
          onClick={handleSubmit(handleSave)}>
          {t('common.save')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        title={confirmDialogTitle}
        message={confirmDialogMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDialogPostiveFn}
        negativeAction={confirmDialogNegativeFn}
      />
    </RootDialog>
  );
}
